import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../integrations/supabase/client';

interface PlatformAdminRole {
  id: string;
  user_id: string;
  role: 'super_admin' | 'platform_admin' | 'customer_admin' | 'user_manager';
  permissions: {
    can_manage_tenants: boolean;
    can_view_analytics: boolean;
    can_send_emails: boolean;
    can_modify_onboarding: boolean;
    can_impersonate_users: boolean;
    can_manage_admins: boolean;
    /** Platform admins and super admins can view client portals */
    can_view_client_portal: boolean;
  };
  created_at: string;
}

export const usePlatformAdmin = () => {
  const { user, session, loading: authLoading } = useAuth();
  const [adminRole, setAdminRole] = useState<PlatformAdminRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  const fetchAdminRole = useCallback(async (userId: string, accessToken: string | undefined) => {
    try {
      
      
      // Use the RPC function which has SECURITY DEFINER to bypass RLS
      // This is more reliable than querying user_roles directly
      const { data: hasSuper, error: superError } = await supabase.rpc('has_role', {
        _user_id: userId,
        _role: 'super_admin'
      });
      
      if (superError) {
        console.error('[usePlatformAdmin] Error checking super_admin role:', superError);
      }
      
      if (hasSuper) {
        
        return {
          id: 'super_admin',
          user_id: userId,
          role: 'super_admin' as const,
          permissions: {
            can_manage_tenants: true,
            can_view_analytics: true,
            can_send_emails: true,
            can_modify_onboarding: true,
            can_impersonate_users: true,
            can_manage_admins: true,
            can_view_client_portal: true,
          },
          created_at: new Date().toISOString(),
        };
      }
      
      // Check platform_admin
      const { data: hasPlatform, error: platformError } = await supabase.rpc('has_role', {
        _user_id: userId,
        _role: 'platform_admin'
      });
      
      if (platformError) {
        console.error('[usePlatformAdmin] Error checking platform_admin role:', platformError);
      }
      
      if (hasPlatform) {
        
        return {
          id: 'platform_admin',
          user_id: userId,
          role: 'platform_admin' as const,
          permissions: {
            can_manage_tenants: true,
            can_view_analytics: true,
            can_send_emails: true,
            can_modify_onboarding: true,
            can_impersonate_users: false,
            can_manage_admins: false,
            can_view_client_portal: true,
          },
          created_at: new Date().toISOString(),
        };
      }
      
      
      return null;
    } catch (error) {
      console.error('[usePlatformAdmin] Error checking admin role:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;

    // No session / no user: not an admin context
    if (!session || !user?.id) {
      setAdminRole(null);
      setLoading(false);
      return;
    }

    const doFetch = async () => {
      setLoading(true);
      const role = await fetchAdminRole(user.id, session.access_token);
      
      // If no role found and we haven't retried yet, try once more after a short delay
      // This handles race conditions where the session might not be fully established
      if (!role && retryCount < 1) {
        
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, 500);
      } else {
        setAdminRole(role);
        setLoading(false);
      }
    };

    doFetch();
  }, [authLoading, session?.access_token, user?.id, retryCount, fetchAdminRole]);

  const isSuperAdmin = adminRole?.role === 'super_admin';
  const isPlatformAdmin = adminRole?.role === 'platform_admin' || isSuperAdmin;
  const isPlatformSupport = false; // Not implemented
  const hasAdminAccess = isPlatformAdmin || isSuperAdmin;

  return {
    adminRole,
    loading,
    isSuperAdmin,
    isPlatformAdmin,
    isPlatformSupport,
    hasAdminAccess,
    permissions: adminRole?.permissions || null,
  };
};
